/**
 * Starter templates for forms (FORMS_DESIGN §4.4): code constants with Greek (formal εσείς) and
 * English copy, like the automations template library. Each template is a complete, valid
 * `FormDefinition` with suggested page blocks. Interest templates include the marketing-email
 * consent box (without it the business cannot legally follow up with people who have no
 * marketing consent); «Γευσιγνωσία» asks about allergies WITH an explicit Art. 9 consent field.
 */
import {
  FormDefinitionSchema,
  defaultFormTheme,
  type FormDefinition,
  type FormDefinitionInput,
  type FormKind,
} from './forms';

type L = 'el' | 'en';

export const FORM_TEMPLATE_KEYS = [
  'new_product',
  'new_service',
  'preorder',
  'interest_survey',
  'party',
  'workshop',
  'tasting',
  'open_day',
] as const;
export type FormTemplateKey = (typeof FORM_TEMPLATE_KEYS)[number];

export interface FormTemplate {
  key: FormTemplateKey;
  kind: FormKind;
  name: string;
  title: string;
  description: string;
  definition: FormDefinition;
  /** Suggested retention (days after close) — e.g. 30 for sensitive answers. */
  suggestedRetentionDays: number | null;
}

interface Copy {
  name: string;
  title: string;
  description: string;
  subtitle: string;
  paragraph: string;
  primaryLabel: string;
  extra: FormDefinitionInput['fields'];
  thanksPositive: string;
  thanksNegative: string;
  retention?: number;
}

const marketingConsent = (locale: L) => ({
  id: 'f_mkemail1',
  key: 'marketing_email',
  type: 'consent' as const,
  label: locale === 'el' ? 'Ενημέρωση με email' : 'Email updates',
  purpose: 'marketing_email' as const,
  statement:
    locale === 'el'
      ? 'Θέλω να ενημερωθώ με email όταν κυκλοφορήσει και για σχετικά νέα της επιχείρησης. Μπορώ να διαγραφώ οποτεδήποτε.'
      : 'I want an email when it launches and related news from the business. I can unsubscribe at any time.',
});

const nameField = (locale: L) => ({
  id: 'f_crmname1',
  key: 'full_name',
  type: 'crm' as const,
  label: locale === 'el' ? 'Ονοματεπώνυμο' : 'Full name',
  target: 'full_name',
  rule: 'if_missing' as const,
});

const positive = (primaryId: string, kind: FormKind) => ({
  fieldId: primaryId,
  op: 'in' as const,
  value: kind === 'interest' ? ['interested', 'maybe'] : ['going', 'maybe'],
});

function copyFor(key: FormTemplateKey, locale: L): Copy {
  const el = locale === 'el';
  switch (key) {
    case 'new_product':
      return {
        name: el ? 'Νέο προϊόν' : 'New product',
        title: el ? 'Έρχεται κάτι νέο' : 'Something new is coming',
        description: el
          ? 'Ανακοινώστε ένα προϊόν και δείτε ποιοι ενδιαφέρονται.'
          : 'Announce a product and see who is interested.',
        subtitle: el
          ? 'Θέλουμε να μάθουμε αν σας ενδιαφέρει πριν κυκλοφορήσει.'
          : 'We would like to know if you are interested before it launches.',
        paragraph: el
          ? 'Ετοιμάζουμε κάτι που πιστεύουμε ότι θα σας αρέσει. Πείτε μας τη γνώμη σας με δύο κλικ.'
          : 'We are preparing something we think you will love. Tell us what you think in two clicks.',
        primaryLabel: el
          ? 'Σας ενδιαφέρει το νέο προϊόν;'
          : 'Are you interested in the new product?',
        extra: [
          {
            id: 'f_npwhen01',
            key: 'when',
            type: 'single_choice',
            label: el ? 'Πότε θα το θέλατε;' : 'When would you want it?',
            options: [
              { id: 'o_now', label: el ? 'Αμέσως' : 'Right away' },
              { id: 'o_month', label: el ? 'Μέσα στον μήνα' : 'Within a month' },
              { id: 'o_later', label: el ? 'Αργότερα' : 'Later' },
            ],
            showIf: positive('f_primary1', 'interest'),
          },
          {
            id: 'f_comment1',
            key: 'comments',
            type: 'long_text',
            label: el ? 'Σχόλια ή ερωτήσεις' : 'Comments or questions',
            maxLength: 1000,
          },
        ],
        thanksPositive: el
          ? 'Σας ευχαριστούμε! Θα σας ενημερώσουμε μόλις κυκλοφορήσει.'
          : 'Thank you! We will let you know as soon as it launches.',
        thanksNegative: el
          ? 'Σας ευχαριστούμε για την απάντηση.'
          : 'Thank you for letting us know.',
      };
    case 'new_service':
      return {
        name: el ? 'Νέα υπηρεσία' : 'New service',
        title: el ? 'Μια νέα υπηρεσία για εσάς' : 'A new service for you',
        description: el
          ? 'Δοκιμάστε το ενδιαφέρον για μια υπηρεσία πριν την ξεκινήσετε.'
          : 'Test interest in a service before you launch it.',
        subtitle: el ? 'Θα θέλατε να τη δοκιμάσετε;' : 'Would you like to try it?',
        paragraph: el
          ? 'Σκεφτόμαστε να προσθέσουμε μια νέα υπηρεσία και η γνώμη σας μετράει.'
          : 'We are thinking of adding a new service and your opinion matters.',
        primaryLabel: el ? 'Θα σας ενδιέφερε;' : 'Would you be interested?',
        extra: [
          {
            id: 'f_nsday001',
            key: 'preferred_day',
            type: 'multi_choice',
            label: el ? 'Ποιες ημέρες σας βολεύουν;' : 'Which days suit you?',
            options: [
              { id: 'o_weekday', label: el ? 'Καθημερινές' : 'Weekdays' },
              { id: 'o_sat', label: el ? 'Σάββατο' : 'Saturday' },
              { id: 'o_evening', label: el ? 'Απογεύματα' : 'Evenings' },
            ],
            showIf: positive('f_primary1', 'interest'),
          },
        ],
        thanksPositive: el
          ? 'Σας ευχαριστούμε! Θα επικοινωνήσουμε μαζί σας.'
          : 'Thank you! We will be in touch.',
        thanksNegative: el ? 'Σας ευχαριστούμε για την απάντηση.' : 'Thank you for your answer.',
      };
    case 'preorder':
      return {
        name: el ? 'Προπαραγγελία' : 'Pre-order',
        title: el ? 'Κλείστε το δικό σας' : 'Reserve yours',
        description: el
          ? 'Μαζέψτε προπαραγγελίες χωρίς πληρωμή.'
          : 'Collect pre-orders without payment.',
        subtitle: el
          ? 'Δηλώστε ενδιαφέρον και σας κρατάμε ένα.'
          : 'Register your interest and we will keep one for you.',
        paragraph: el
          ? 'Οι ποσότητες είναι περιορισμένες. Η δήλωση δεν σας δεσμεύει.'
          : 'Quantities are limited. Registering does not commit you.',
        primaryLabel: el ? 'Θέλετε να σας κρατήσουμε ένα;' : 'Shall we keep one for you?',
        extra: [
          {
            id: 'f_poqty001',
            key: 'quantity',
            type: 'number',
            label: el ? 'Πόσα τεμάχια;' : 'How many?',
            min: 1,
            max: 20,
            integer: true,
            showIf: positive('f_primary1', 'interest'),
          },
        ],
        thanksPositive: el
          ? 'Σας ευχαριστούμε! Κρατήσαμε τη δήλωσή σας.'
          : 'Thank you! Your request is noted.',
        thanksNegative: el ? 'Σας ευχαριστούμε.' : 'Thank you.',
      };
    case 'interest_survey':
      return {
        name: el ? 'Έρευνα ενδιαφέροντος' : 'Interest survey',
        title: el ? 'Πείτε μας τι θέλετε' : 'Tell us what you want',
        description: el
          ? 'Ρωτήστε τους πελάτες σας τι θα ήθελαν να δουν.'
          : 'Ask your customers what they would like to see.',
        subtitle: el ? 'Μας παίρνει ένα λεπτό.' : 'It takes a minute.',
        paragraph: el
          ? 'Θέλουμε να φέρουμε περισσότερα από όσα αγαπάτε.'
          : 'We want to bring more of what you love.',
        primaryLabel: el ? 'Θα σας ενδιέφεραν νέες επιλογές;' : 'Would new options interest you?',
        extra: [
          {
            id: 'f_ispick01',
            key: 'topics',
            type: 'multi_choice',
            label: el ? 'Τι σας ενδιαφέρει περισσότερο;' : 'What interests you most?',
            options: [
              { id: 'o_new', label: el ? 'Νέα προϊόντα' : 'New products' },
              { id: 'o_offers', label: el ? 'Προσφορές' : 'Offers' },
              { id: 'o_events', label: el ? 'Εκδηλώσεις' : 'Events' },
            ],
          },
          {
            id: 'f_israte01',
            key: 'satisfaction',
            type: 'rating',
            label: el ? 'Πόσο ικανοποιημένοι είστε σήμερα;' : 'How satisfied are you today?',
            scale: 'stars5',
          },
        ],
        thanksPositive: el ? 'Σας ευχαριστούμε πολύ!' : 'Thank you very much!',
        thanksNegative: el ? 'Σας ευχαριστούμε πολύ!' : 'Thank you very much!',
      };
    case 'party':
      return {
        name: el ? 'Εκδήλωση / πάρτι' : 'Event / party',
        title: el ? 'Σας προσκαλούμε' : 'You are invited',
        description: el
          ? 'Προσκαλέστε πελάτες και δείτε ποιοι έρχονται.'
          : 'Invite customers and see who is coming.',
        subtitle: el ? 'Θα χαρούμε πολύ να σας δούμε.' : 'We would love to see you.',
        paragraph: el
          ? 'Ελάτε να γιορτάσουμε μαζί. Πείτε μας αν θα έρθετε για να ετοιμαστούμε.'
          : 'Come and celebrate with us. Let us know if you are coming so we can prepare.',
        primaryLabel: el ? 'Θα έρθετε;' : 'Will you come?',
        extra: [],
        thanksPositive: el ? 'Τέλεια! Σας περιμένουμε.' : 'Great! See you there.',
        thanksNegative: el
          ? 'Κρίμα! Ευχαριστούμε που μας ενημερώσατε.'
          : 'What a pity! Thank you for letting us know.',
      };
    case 'workshop':
      return {
        name: el ? 'Εργαστήριο' : 'Workshop',
        title: el ? 'Εργαστήριο' : 'Workshop',
        description: el ? 'Περιορισμένες θέσεις, δηλώσεις συμμετοχής.' : 'Limited seats, sign-ups.',
        subtitle: el ? 'Οι θέσεις είναι περιορισμένες.' : 'Seats are limited.',
        paragraph: el
          ? 'Ένα πρακτικό εργαστήριο σε μικρή ομάδα. Κλείστε τη θέση σας.'
          : 'A hands-on workshop in a small group. Book your seat.',
        primaryLabel: el ? 'Θα συμμετέχετε;' : 'Will you take part?',
        extra: [
          {
            id: 'f_wslevel1',
            key: 'level',
            type: 'single_choice',
            label: el ? 'Ποιο είναι το επίπεδό σας;' : 'What is your level?',
            options: [
              { id: 'o_beginner', label: el ? 'Αρχάριος' : 'Beginner' },
              { id: 'o_some', label: el ? 'Λίγη εμπειρία' : 'Some experience' },
              { id: 'o_advanced', label: el ? 'Προχωρημένος' : 'Advanced' },
            ],
            showIf: positive('f_primary1', 'event'),
          },
        ],
        thanksPositive: el
          ? 'Η θέση σας κρατήθηκε. Τα λέμε σύντομα!'
          : 'Your seat is booked. See you soon!',
        thanksNegative: el ? 'Ευχαριστούμε για την ενημέρωση.' : 'Thank you for letting us know.',
      };
    case 'tasting':
      return {
        name: el ? 'Γευσιγνωσία' : 'Tasting',
        title: el ? 'Βραδιά γευσιγνωσίας' : 'Tasting evening',
        description: el
          ? 'Με ερώτηση για αλλεργίες και ρητή συναίνεση.'
          : 'With an allergies question and explicit consent.',
        subtitle: el
          ? 'Μια βραδιά με γεύσεις και καλή παρέα.'
          : 'An evening of flavours and good company.',
        paragraph: el
          ? 'Δοκιμάστε τις νέες μας γεύσεις. Πείτε μας αν θα έρθετε και αν έχετε κάποια αλλεργία.'
          : 'Taste our new flavours. Let us know if you are coming and if you have any allergies.',
        primaryLabel: el ? 'Θα έρθετε;' : 'Will you come?',
        extra: [
          {
            id: 'f_tzallg01',
            key: 'allergies',
            type: 'short_text',
            label: el ? 'Αλλεργίες ή διατροφικοί περιορισμοί' : 'Allergies or dietary restrictions',
            maxLength: 200,
            showIf: positive('f_primary1', 'event'),
          },
          {
            id: 'f_tzcons01',
            key: 'health_consent',
            type: 'consent',
            label: el ? 'Συναίνεση για στοιχεία υγείας' : 'Consent for health information',
            purpose: 'special_category',
            required: true,
            showIf: positive('f_primary1', 'event'),
          },
        ],
        thanksPositive: el ? 'Σας περιμένουμε!' : 'We look forward to seeing you!',
        thanksNegative: el ? 'Ευχαριστούμε για την ενημέρωση.' : 'Thank you for letting us know.',
        retention: 30,
      };
    case 'open_day':
      return {
        name: el ? 'Ημέρα γνωριμίας' : 'Open day',
        title: el ? 'Ημέρα γνωριμίας' : 'Open day',
        description: el
          ? 'Ανοιχτή πόρτα για νέους και παλιούς πελάτες.'
          : 'An open door for new and existing customers.',
        subtitle: el ? 'Ελάτε να γνωριστούμε.' : 'Come and meet us.',
        paragraph: el
          ? 'Σας ανοίγουμε τον χώρο μας για να γνωριστούμε από κοντά.'
          : 'We open our doors so we can meet in person.',
        primaryLabel: el ? 'Θα περάσετε;' : 'Will you drop by?',
        extra: [],
        thanksPositive: el ? 'Ανυπομονούμε να σας δούμε!' : 'We look forward to seeing you!',
        thanksNegative: el ? 'Ευχαριστούμε για την ενημέρωση.' : 'Thank you for letting us know.',
      };
  }
}

export function formTemplateKind(key: FormTemplateKey): FormKind {
  return ['party', 'workshop', 'tasting', 'open_day'].includes(key) ? 'event' : 'interest';
}

/** A template rendered in a locale (with an optional brand colour for the page theme). */
export function formTemplate(
  key: FormTemplateKey,
  locale: L = 'el',
  brandColor?: string | null,
): FormTemplate {
  const kind = formTemplateKind(key);
  const c = copyFor(key, locale);
  const primary =
    kind === 'interest'
      ? { id: 'f_primary1', key: 'interest', type: 'interest' as const, label: c.primaryLabel }
      : { id: 'f_primary1', key: 'rsvp', type: 'rsvp' as const, label: c.primaryLabel };
  const fields: FormDefinitionInput['fields'] = [primary, nameField(locale), ...c.extra];
  if (kind === 'interest') fields.push(marketingConsent(locale));
  const blocks: unknown[] = [
    { id: 'logo', type: 'logo' },
    { id: 'hero', type: 'hero', title: c.title, subtitle: c.subtitle },
  ];
  if (kind === 'event') blocks.push({ id: 'event', type: 'event_details' });
  blocks.push({ id: 'intro', type: 'paragraph', text: c.paragraph });
  const definition = FormDefinitionSchema.parse({
    fields,
    page: { theme: defaultFormTheme(brandColor), blocks, footerBlocks: [] },
    texts: { thanksPositive: c.thanksPositive, thanksNegative: c.thanksNegative },
  });
  return {
    key,
    kind,
    name: c.name,
    title: c.title,
    description: c.description,
    definition,
    suggestedRetentionDays: c.retention ?? null,
  };
}

export function formTemplatesFor(kind: FormKind, locale: L = 'el'): FormTemplate[] {
  return FORM_TEMPLATE_KEYS.filter((k) => formTemplateKind(k) === kind).map((k) =>
    formTemplate(k, locale),
  );
}
